import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, Modale, Riquadro, Spunta, Tabella,
} from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * Regola di cancellazione della posta e sezioni «In cancellazione» e «In conservazione» (500).
 *
 * La regola la decide CIDIS: spenta per difetto, si cambia con una richiesta che la Referente privacy
 * (o il DPO) approva in «Approvazioni». In cima a «In cancellazione» c'e' la regola in vigore, per tutti; chi ha
 * `impostazione.modifica` ne chiede il cambio da qui. Sotto, le conversazioni delle proprie caselle
 * (la propria, quelle di gruppo; la casella dell'ente per chi la gestisce) che la regola cancellera',
 * con la data, e il comando «Metti in conservazione». In «In conservazione» quelle tolte dalla regola,
 * con chi, quando e perche', e il comando «Togli dalla conservazione».
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
const data = (iso: string) => giorno.format(new Date(iso))

const NOMI_RUOLI: Record<string, string> = { DPO: 'una Referente privacy o un DPO in carico' }

const conversazioni = (n: number) => (n === 1 ? 'una conversazione' : `${n} conversazioni`)

/** Webmail spenta: le sezioni non si aprono, la regola si legge comunque. */
function WebmailSpenta() {
  return (
    <Avviso tipo="informazione">
      La funzione «Webmail e archiviazione integrale della posta» non è attiva: le conversazioni
      archiviate non si vedono. La regola resta in vigore per quello che è già archiviato.
    </Avviso>
  )
}

/** Oggetto e partecipanti, come nell'elenco della webmail. */
function Conversazione({ voce }: { voce: posta.VoceThread }) {
  return (
    <>
      <div className="voce-principale">{voce.oggetto || '(senza oggetto)'}</div>
      <div className="piccolo tenue">
        {voce.partecipanti.map(posta.nomeBreve).join(', ') || '—'}
        {voce.messaggi > 1 && ` · ${voce.messaggi} messaggi`}
      </div>
    </>
  )
}

// --- regola -----------------------------------------------------------------------------------

function RiquadroRegola({ stato, ricarica }: { stato: posta.StatoRegola; ricarica: () => void }) {
  const [modulo, setModulo] = useState(false)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const { regola, richiesta } = stato
  const inAttesa = richiesta?.stato === 'in_attesa'

  async function ritira() {
    if (!richiesta) return
    setInCorso(true); setErrore(null)
    try { await posta.ritiraRichiestaRegola(richiesta.id); ricarica() }
    catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Riquadro
      titolo="Regola di cancellazione"
      azioni={
        <>
          <Etichetta tono={regola.attiva ? 'attenzione' : 'neutro'}>{regola.attiva ? 'accesa' : 'spenta'}</Etichetta>
          {stato.puo_chiedere && !inAttesa && (
            <Bottone dimensione="piccolo" onClick={() => setModulo(true)}>Cambia la regola</Bottone>
          )}
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>
        {regola.attiva
          ? <>Si cancellano le conversazioni il cui ultimo messaggio ha più di {regola.giorni} giorni, e quelle
              rimaste nel cestino per 30 giorni.
              Ognuna resta qui almeno {regola.preavviso_giorni} giorni prima di essere cancellata. Le
              conversazioni collegate a una scheda {regola.comprende_schede ? 'sono comprese' : 'restano fuori'}.</>
          : <>Nessuna conversazione si cancella: l’archivio tiene tutto, senza limiti di spazio.</>}
        {regola.in_vigore_dal && <span className="tenue"> In vigore dal {data(regola.in_vigore_dal)}.</span>}
      </p>
      {stato.puo_chiedere && (
        <p className="piccolo tenue">
          Il cambio della regola lo decide la Referente privacy (o il DPO) nella pagina «Approvazioni» e vale da quando è
          approvato. In tutto: {conversazioni(stato.conteggi.in_cancellazione)} in cancellazione,{' '}
          {stato.conteggi.in_conservazione} in conservazione.
        </p>
      )}
      {richiesta && (
        <Avviso tipo={richiesta.stato === 'respinta' ? 'attenzione' : 'informazione'}>
          {richiesta.stato === 'in_attesa' && <>In attesa della Referente privacy: </>}
          {richiesta.stato === 'approvata' && <>Approvato il {data(richiesta.decisa_il ?? richiesta.richiesta_il)}: </>}
          {richiesta.stato === 'respinta' && <>Respinto il {data(richiesta.decisa_il ?? richiesta.richiesta_il)}: </>}
          {richiesta.stato === 'scaduta' && <>Scaduto senza decisione: </>}
          {richiesta.stato === 'annullata' && <>Ritirato: </>}
          {richiesta.regola ? posta.regolaATesto(richiesta.regola) : 'cambio della regola'}.
          <span className="tenue"> Chiesto da {richiesta.richiedente} il {data(richiesta.richiesta_il)}.</span>
          {richiesta.nota_decisione && <> Nota: «{richiesta.nota_decisione}».</>}
          {inAttesa && richiesta.mia && (
            <div className="riga">
              <Bottone dimensione="piccolo" variante="discreto" inCorso={inCorso} onClick={() => void ritira()}>
                Ritira la richiesta
              </Bottone>
              <Link to="/approvazioni" className="piccolo">Vai alle approvazioni</Link>
            </div>
          )}
        </Avviso>
      )}
      {modulo && (
        <ModuloRegola stato={stato} chiudi={() => setModulo(false)}
                      inviata={() => { setModulo(false); ricarica() }} />
      )}
    </Riquadro>
  )
}

function ModuloRegola({ stato, chiudi, inviata }: {
  stato: posta.StatoRegola; chiudi: () => void; inviata: () => void
}) {
  const r = stato.regola
  const [attiva, setAttiva] = useState(!r.attiva)
  const [giorni, setGiorni] = useState(String(r.giorni))
  const [preavviso, setPreavviso] = useState(String(r.preavviso_giorni))
  const [schede, setSchede] = useState(r.comprende_schede)
  const [motivazione, setMotivazione] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [gMin, gMax] = stato.limiti.giorni
  const [pMin, pMax] = stato.limiti.preavviso_giorni

  const g = Number(giorni)
  const p = Number(preavviso)
  const erroreGiorni = Number.isInteger(g) && g >= gMin && g <= gMax ? null : `Fra ${gMin} e ${gMax} giorni.`
  const errorePreavviso = !(Number.isInteger(p) && p >= pMin && p <= pMax) ? `Fra ${pMin} e ${pMax} giorni.`
    : p >= g ? 'Più breve del termine.' : null
  const proposta: posta.RegolaCancellazione = { attiva, giorni: g, preavviso_giorni: p, comprende_schede: schede }
  const uguale = !attiva && !r.attiva
    || (attiva === r.attiva && g === r.giorni && p === r.preavviso_giorni && schede === r.comprende_schede)
  const valida = !erroreGiorni && !errorePreavviso && !uguale && motivazione.trim().length >= 20
    && stato.decisori_mancanti.length === 0

  async function invia() {
    setErrore(null); setInCorso(true)
    try {
      await posta.chiediRegola({ ...proposta, motivazione: motivazione.trim() })
      inviata()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Cambia la regola di cancellazione" chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={!valida} onClick={() => void invia()}>
            Chiedi l’approvazione
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {stato.decisori_mancanti.length > 0 && (
        <Avviso tipo="attenzione">
          Manca {stato.decisori_mancanti.map((x) => NOMI_RUOLI[x] ?? x).join(' e ')}: senza, la
          richiesta non si può aprire.
        </Avviso>
      )}
      <p className="piccolo tenue">
        La regola nuova vale da quando la Referente privacy (o il DPO) la approva. Le conversazioni che entrano in
        cancellazione si cancellano non prima del preavviso, anche se sono più vecchie del termine; una
        data già annunciata non si anticipa.
      </p>
      <Spunta etichetta="Regola accesa" valore={attiva} cambia={setAttiva}
              aiuto="Spenta, nessuna conversazione si cancella e la sezione «In cancellazione» si svuota." />
      <Campo tipo="number" etichetta="Cancella oltre (giorni dall’ultimo messaggio)" obbligatorio
             valore={giorni} cambia={setGiorni} min={gMin} max={gMax} passi={1}
             disabilitato={!attiva} errore={attiva ? erroreGiorni : null}
             aiuto={`Fra ${gMin} e ${gMax}. Vale per l’intera conversazione: conta il suo messaggio più recente.`} />
      <Campo tipo="number" etichetta="Preavviso (giorni)" obbligatorio valore={preavviso} cambia={setPreavviso}
             min={pMin} max={pMax} passi={1} disabilitato={!attiva} errore={attiva ? errorePreavviso : null}
             aiuto={`Fra ${pMin} e ${pMax}. I titolari e i membri delle caselle ricevono un avviso quando conversazioni loro entrano in cancellazione.`} />
      <Spunta etichetta="Comprende le conversazioni collegate a una scheda" valore={schede} cambia={setSchede}
              disabilitato={!attiva}
              aiuto="Di solito no: i tempi di conservazione delle schede non sono ancora fissati." />
      <AreaTesto etichetta="Motivazione" obbligatorio righe={3} maxLength={1000}
                 valore={motivazione} cambia={setMotivazione}
                 aiuto="Almeno 20 caratteri. La legge chi decide." />
      {!uguale && (
        <p className="piccolo">Regola proposta: {posta.regolaATesto(proposta)}.</p>
      )}
    </Modale>
  )
}

// --- sezioni ----------------------------------------------------------------------------------

/** Stato di un elenco a pagine: righe, cursore della pagina dopo, totale. */
function usaSezione<T extends { id: string }>(
  carica: (dopo: string | null) => Promise<posta.PaginaSezione<T>>,
) {
  const [voci, setVoci] = useState<T[] | null>(null)
  const [totale, setTotale] = useState(0)
  const [prossimo, setProssimo] = useState<string | null>(null)
  const [spenta, setSpenta] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  const leggi = useCallback(async (dopo: string | null) => {
    setErrore(null)
    try {
      const p = await carica(dopo)
      setVoci((v) => (dopo && v ? [...v, ...p.conversazioni] : p.conversazioni))
      setTotale(p.totale)
      setProssimo(p.prossimo)
    } catch (e) {
      if (posta.interruttoreSpento(e)) setSpenta(true)
      else setErrore(testoErrore(e))
    }
  }, [carica])

  useEffect(() => { void leggi(null) }, [leggi])

  const togli = (id: string) => {
    setVoci((v) => v?.filter((x) => x.id !== id) ?? v)
    setTotale((n) => Math.max(0, n - 1))
  }
  return { voci, totale, prossimo, spenta, errore, leggi, togli }
}

export function InCancellazione() {
  const [stato, setStato] = useState<posta.StatoRegola | null>(null)
  const [erroreRegola, setErroreRegola] = useState<string | null>(null)
  const [scelta, setScelta] = useState<posta.VoceInCancellazione | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const sezione = usaSezione(posta.elencaInCancellazione)

  const caricaRegola = useCallback(() => {
    posta.leggiRegola().then(setStato).catch((e) => setErroreRegola(testoErrore(e)))
  }, [])
  useEffect(caricaRegola, [caricaRegola])

  return (
    <>
      {erroreRegola && <Avviso tipo="errore">{erroreRegola}</Avviso>}
      {stato && <RiquadroRegola stato={stato} ricarica={() => { caricaRegola(); void sezione.leggi(null) }} />}

      <Riquadro titolo="In cancellazione"
        azioni={sezione.voci && <span className="piccolo tenue">{conversazioni(sezione.totale)}</span>}>
        {sezione.spenta ? <WebmailSpenta /> : (
          <>
            {sezione.errore && <Avviso tipo="errore">{sezione.errore}</Avviso>}
            {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
            <p className="piccolo tenue">
              Le conversazioni delle tue caselle che la regola cancellerà, dalla data più vicina, comprese
              quelle nel cestino da 30 giorni. Un messaggio nuovo nella conversazione la toglie da qui, come
              «Ripristina» dal cestino; «Metti in conservazione» la toglie dalla regola.
            </p>
            {!sezione.voci ? <Attesa /> : (
              <Tabella intestazioni={['Conversazione', 'Casella', 'Ultimo messaggio', 'Si cancella il',
                                      <span className="solo-lettore" key="azioni">Azioni</span>]}
                       vuoto={<Vuoto testo="Nessuna conversazione in cancellazione." />}>
                {sezione.voci.map((v) => (
                  <tr key={v.id}>
                    <td data-etichetta="Conversazione">
                      <Conversazione voce={v} />
                      {v.dal_cestino && <Etichetta>dal cestino</Etichetta>}
                    </td>
                    <td data-etichetta="Casella" className="piccolo">{v.casella}</td>
                    <td data-etichetta="Ultimo messaggio" className="piccolo">{data(v.ultimo_il)}</td>
                    <td data-etichetta="Si cancella il">{data(v.prevista_il)}</td>
                    <td data-etichetta="Azioni">
                      <Bottone dimensione="piccolo" onClick={() => { setMessaggio(null); setScelta(v) }}>
                        Metti in conservazione
                      </Bottone>
                    </td>
                  </tr>
                ))}
              </Tabella>
            )}
            {sezione.prossimo && (
              <Bottone variante="discreto" onClick={() => void sezione.leggi(sezione.prossimo)}>Mostra altre</Bottone>
            )}
          </>
        )}
      </Riquadro>

      {scelta && (
        <ModuloConservazione voce={scelta} chiudi={() => setScelta(null)}
          fatto={() => {
            sezione.togli(scelta.id)
            setMessaggio(`«${scelta.oggetto || '(senza oggetto)'}» è in conservazione: la regola non la cancella.`)
            setScelta(null)
          }} />
      )}
    </>
  )
}

function ModuloConservazione({ voce, chiudi, fatto }: {
  voce: posta.VoceInCancellazione; chiudi: () => void; fatto: () => void
}) {
  const [motivazione, setMotivazione] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const valida = motivazione.trim().length >= 5

  async function invia() {
    setErrore(null); setInCorso(true)
    try { await posta.mettiInConservazione(voce.id, motivazione.trim()); fatto() }
    catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Metti in conservazione" chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} disabled={!valida} onClick={() => void invia()}>
            Metti in conservazione
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p><strong>{voce.oggetto || '(senza oggetto)'}</strong> · {voce.casella}</p>
      <p className="piccolo tenue">
        La conversazione non si cancella più finché qualcuno non la toglie dalla conservazione. Chi vede
        la casella legge chi l’ha messa, quando e perché.
      </p>
      <AreaTesto etichetta="Perché si tiene" obbligatorio righe={2} maxLength={300}
                 valore={motivazione} cambia={setMotivazione}
                 aiuto="Breve: pratica aperta, contestazione, obbligo di legge. Niente dati personali." />
    </Modale>
  )
}

export function InConservazione() {
  const [scelta, setScelta] = useState<posta.VoceInConservazione | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const sezione = usaSezione(posta.elencaInConservazione)

  return (
    <>
      <Riquadro titolo="In conservazione"
        azioni={sezione.voci && <span className="piccolo tenue">{conversazioni(sezione.totale)}</span>}>
        {sezione.spenta ? <WebmailSpenta /> : (
          <>
            {sezione.errore && <Avviso tipo="errore">{sezione.errore}</Avviso>}
            {messaggio && <Avviso tipo="successo">{messaggio}</Avviso>}
            <p className="piccolo tenue">
              Le conversazioni delle tue caselle che la regola di cancellazione non tocca, con chi le
              ha messe in conservazione, quando e perché.
            </p>
            {!sezione.voci ? <Attesa /> : (
              <Tabella intestazioni={['Conversazione', 'Casella', 'Messa da', 'Perché',
                                      <span className="solo-lettore" key="azioni">Azioni</span>]}
                       vuoto={<Vuoto testo="Nessuna conversazione in conservazione." />}>
                {sezione.voci.map((v) => (
                  <tr key={v.id}>
                    <td data-etichetta="Conversazione"><Conversazione voce={v} /></td>
                    <td data-etichetta="Casella" className="piccolo">{v.casella}</td>
                    <td data-etichetta="Messa da" className="piccolo">
                      {v.conservata_da ? `${v.conservata_da.nome} ${v.conservata_da.cognome}` : '—'}
                      <div className="tenue">il {data(v.conservata_il)}</div>
                    </td>
                    <td data-etichetta="Perché" className="piccolo">{v.motivazione}</td>
                    <td data-etichetta="Azioni">
                      <Bottone dimensione="piccolo" variante="discreto"
                               onClick={() => { setMessaggio(null); setScelta(v) }}>
                        Togli dalla conservazione
                      </Bottone>
                    </td>
                  </tr>
                ))}
              </Tabella>
            )}
            {sezione.prossimo && (
              <Bottone variante="discreto" onClick={() => void sezione.leggi(sezione.prossimo)}>Mostra altre</Bottone>
            )}
          </>
        )}
      </Riquadro>

      {scelta && (
        <ConfermaTogli voce={scelta} chiudi={() => setScelta(null)}
          fatto={(prevista) => {
            sezione.togli(scelta.id)
            setMessaggio(prevista
              ? `«${scelta.oggetto || '(senza oggetto)'}» è tornata sotto la regola: si cancella non prima del ${data(prevista)}.`
              : `«${scelta.oggetto || '(senza oggetto)'}» è tornata sotto la regola.`)
            setScelta(null)
          }} />
      )}
    </>
  )
}

function ConfermaTogli({ voce, chiudi, fatto }: {
  voce: posta.VoceInConservazione; chiudi: () => void; fatto: (prevista: string | null) => void
}) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function togli() {
    setErrore(null); setInCorso(true)
    try { fatto((await posta.togliDallaConservazione(voce.id)).prevista_il) }
    catch (e) { setErrore(testoErrore(e)) }
    finally { setInCorso(false) }
  }

  return (
    <Modale titolo="Togli dalla conservazione" chiudi={chiudi} fuocoIniziale="azioni"
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso} onClick={() => void togli()}>
            Togli dalla conservazione
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p><strong>{voce.oggetto || '(senza oggetto)'}</strong> · {voce.casella}</p>
      <p className="piccolo tenue">
        Torna sotto la regola di cancellazione. Se la regola la cancella, entra in «In cancellazione»
        con un preavviso pieno a partire da oggi.
      </p>
    </Modale>
  )
}
